"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type WsmTheme = "day" | "night";

const STORAGE_KEY = "wsm-theme";

const WsmThemeContext = createContext<{
  theme: WsmTheme;
  setTheme: (t: WsmTheme) => void;
} | null>(null);

function applyThemeAttr(theme: WsmTheme) {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.wsmTheme = theme;
  }
}

function readSavedTheme(): WsmTheme {
  if (typeof window === "undefined") return "night";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "day" || saved === "night") return saved;
  } catch {
    /* noop */
  }
  return "night";
}

/**
 * 全局白天 / 黑夜模式：只影响亮度与背景插画，不改变页面结构。
 * 偏好保存在本地设备（localStorage），首次进入默认黑夜（与当前视觉基调一致）。
 */
export function WsmThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<WsmTheme>(readSavedTheme);

  useEffect(() => {
    applyThemeAttr(theme);
  }, [theme]);

  const setTheme = useCallback((t: WsmTheme) => {
    setThemeState(t);
    applyThemeAttr(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* noop */
    }
  }, []);

  return <WsmThemeContext.Provider value={{ theme, setTheme }}>{children}</WsmThemeContext.Provider>;
}

export function useWsmTheme() {
  const ctx = useContext(WsmThemeContext);
  if (!ctx) throw new Error("useWsmTheme must be used within WsmThemeProvider");
  return ctx;
}
